import { ChangeDetectionStrategy, Component, ErrorHandler, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { Router } from '@angular/router'

import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { onSelvityspyyntoNakyvissaKayttajalle } from '@lemon/core-firebase/service/tosite/selvityspyynto-nakyvyys'
import { TositeUriService } from '@lemon/core-firebase/service/tosite/tosite-uri.service'
import { KayttajaService } from './_angular/service/kayttaja.service'
import { VersionTarkistusPalvelu } from './_angular/service/version-tarkistus.palvelu'

import { LadataanService } from './_jaettu-angular/service/ladataan.service'

import { Kayttaja, NewFeature, Roolit } from '@lemon/core-firebase/model/kayttaja'
import { Laskuasetukset } from '@lemon/core-firebase/model/lasku'
import { ToistuvaLasku } from '@lemon/core-firebase/model/lasku-toistuva'
import { DateService } from '@lemon/core/service/date.service'

import { AsiakasKyselyDialog, AsiakasKyselyDialogData } from './asetukset/asiakaskysely.dialog'
import { VaihdaKieliDialog, VaihdaKieliDialogData } from './vaihda-kieli.dialog'
import { VaihdaSalasanaDialog, VaihdaSalasanaDialogData } from './vaihda-salasana.dialog'
import { VaihdaYritysDialog, VaihdaYritysDialogData } from './vaihda-yritys.dialog'

import { ApixReceivedInvoiceConfig } from '@lemon/core-firebase/model/apix'
import { Selvityspyynto } from '@lemon/core-firebase/model/selvitettavat'
import { TilinpaatosUserData } from '@lemon/core-firebase/model/tilinpaatos'
import { VaihdettavatYrityksetVastaus } from '@lemon/core-firebase/model/login'
import { combineLatest, firstValueFrom, from, Observable, of as observableOf, Subject } from 'rxjs'
import { catchError, distinctUntilChanged, map, shareReplay, startWith, switchMap, takeUntil } from 'rxjs/operators'
import { FirebaseLemonaid } from './_angular/service/firebase-lemonaid.service'
import { TilinpaatosLemonaidUsersService } from './_angular/service/tilinpaatos-lemonaid-users.service'
import { EinvoicesAngularService } from './_angular/service/tosite/einvoices-angular.service'
import { UserSettingsService } from './_angular/service/user-settings.service'
import { MaksutSharedStateService } from './maksut/maksut-shared-state.service'

type Alavalikko = 'laskutus' | 'tositteet' | 'palkat' | 'none' | 'raportit' | 'maksut'

@Component({
  selector: 'app-menu',
  templateUrl: './app-menu.component.html',
  styleUrls: ['./app-menu.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppMenuComponent implements OnInit, OnDestroy {
  private _kayttaja: Kayttaja
  private _url: string

  @Output() vasemmanValikonSulkupyynto: EventEmitter<void> = new EventEmitter()

  @Input() asetukset: Laskuasetukset = null
  @Input() naytaSuper = false
  @Input()
  set kayttaja(kayttaja: Kayttaja) {
    this._kayttaja = kayttaja
    if (this._kayttaja && this._kayttaja.roolit) {
      this.naytaPalkat = this._kayttaja.roolit[Roolit.PALKAT] === true
      this.naytaLaskutus = this._kayttaja.roolit[Roolit.LASKUTUS] === true
      this.naytaMaksut = this._kayttaja.roolit[Roolit.MAKSUT] === true

      this.naytaTositteetOsto = this._kayttaja.roolit[Roolit.TOSITTEET_OSTO] === true
      this.naytaTositteetMyynti = this._kayttaja.roolit[Roolit.TOSITTEET_MYYNTI] === true
      this.naytaTositteetTiliotteet = this._kayttaja.roolit[Roolit.TOSITTEET_TILI] === true
      this.naytaTositteetPalkkamateriaalit = this._kayttaja.roolit[Roolit.TOSITTEET_PALKKA] === true
      this.naytaLaskujenVastaanotto = this._kayttaja.roolit[Roolit.SAHKOISTEN_LASKUJEN_VASTAANOTTO] === true
      this.naytaTositteet = this.naytaTositteetOsto || this.naytaTositteetMyynti || this.naytaTositteetTiliotteet || this.naytaTositteetPalkkamateriaalit
      this.naytaSopimus = this._kayttaja.roolit[Roolit.HALLINTO_SOPIMUKSEN_HYVAKSYJA] === true
      this.naytaKirjanpito = this._kayttaja.roolit[Roolit.RAPORTIT] === true
    } else {
      this.naytaPalkat = false
      this.naytaLaskutus = false
      this.naytaMaksut = false

      this.naytaTositteet = false
      this.naytaTositteetOsto = false
      this.naytaTositteetMyynti = false
      this.naytaTositteetTiliotteet = false
      this.naytaTositteetPalkkamateriaalit = false
      this.naytaSopimus = false
      this.naytaKirjanpito = false
    }
  }
  get kayttaja(): Kayttaja {
    return this._kayttaja
  }

  @Input()
  set url(url: string) {
    this._url = url || ''
    if (this._url.startsWith('/laskutus/') || this._url.startsWith('/asetukset') || this._url === '/') {
      this.valittuAlavalikko = 'laskutus'
    } else if (this._url.startsWith('/tositteet/')) {
      this.valittuAlavalikko = 'tositteet'
    } else if (this._url.startsWith('/palkat')) {
      this.valittuAlavalikko = 'palkat'
    } else if (this._url.startsWith('/tilinpaatokset') || this._url.startsWith('/kirjanpidon-raportit')) {
      this.valittuAlavalikko = 'raportit'
    } else if (this._url.startsWith('/maksut')) {
      this.valittuAlavalikko = 'maksut'
    } else {
      this.valittuAlavalikko = 'none'
    }
  }
  get url(): string {
    return this._url
  }

  lemonaidAltTextObservable: Observable<string>
  naytaPankitObservable: Observable<boolean>
  selvitettaviaTositteitaObservable: Observable<number>

  private _apixConfigObservable: Observable<ApixReceivedInvoiceConfig>

  // showNewFeatureSignAtLaskujenVastaanottoObservable: Observable<boolean>
  // showNewFeatureSignAtMaksutLaskujenVastaanottoObservable: Observable<boolean>
  // showNewFeatureSignAtMaksutMaksaminenObservable: Observable<boolean>
  // showNewFeatureSignAtLaskutusAsetuksetObservable: Observable<boolean>
  numberOfUnapprovedEinvoicesObservable: Observable<number>
  einvoiceApprovingActivatedObservable: Observable<boolean>
  naytaTilinpaatoksetObservable: Observable<boolean>
  naytaKerroMielipiteesiNappiObservable: Observable<boolean>

  tositteetRequiringAttentionObservable: Observable<number>

  maksutEraantyneetCountObservable: Observable<number>
  maksutAvoimetCountObservable: Observable<number>

  onToistuviaLaskujaObservable: Observable<boolean>

  vaihdettavatYrityksetObservable: Observable<VaihdettavatYrityksetVastaus | null>

  // sopimuskausiTauolla: boolean = false

  naytaTositteet = false
  naytaTositteetOsto = false
  naytaTositteetMyynti = false
  naytaTositteetTiliotteet = false
  naytaTositteetPalkkamateriaalit = false
  naytaLaskujenVastaanotto = false
  naytaLaskujenHyvaksyminen = false
  naytaSopimus = false
  naytaMaksut = false
  naytaKirjanpito = false
  // naytaTinkAutentikointi = false
  // toggleMomentumScrollingOff = false
  naytaPalkat = false
  naytaLaskutus = false
  ylapalkkiNakyvissa = false
  vasenValikkoAuki = false
  vasemmanValikonTila: 'side' | 'over' = 'side'
  internetYhteys: 'hide' | 'show' = 'hide'
  tekstit = ''
  valittuAlavalikko: Alavalikko = 'laskutus'

  private _ngUnsubscribe: Subject<void> = new Subject<void>()

  constructor(
    private _errorHandler: ErrorHandler,
    private _kayttajaService: KayttajaService,
    private _router: Router,
    private _dialog: MatDialog,
    private _ladataanService: LadataanService,
    private _versionTarkistusPalvelu: VersionTarkistusPalvelu,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _einvoicesAngularService: EinvoicesAngularService,
    private _maksutSharedStateService: MaksutSharedStateService,
    private _userSettingsService: UserSettingsService,
    private _tilinpaatosLemonaidUsersService: TilinpaatosLemonaidUsersService,
    private _dateService: DateService,
    private _laskuUriService: LaskuUriService,
    private _tositeUriService: TositeUriService,
  ) {
    this.maksutEraantyneetCountObservable = this._maksutSharedStateService.getDataSource().eraantyneetCountObservable
    this.maksutAvoimetCountObservable = this._maksutSharedStateService.getDataSource().avoimetCountObservable

    this.lemonaidAltTextObservable = this._versionTarkistusPalvelu.sovelluksenVersioObservable.pipe(
      map(versio => {
        if (versio) {
          console.log('Sovelluksen versio: ' + versio.binaarinversio + ', viimeisin: ' + versio.viimeisinversio)
          return 'Lemonaid ' + versio.binaarinversio
        }
        return 'Lemonaid'
      }),
      startWith('Lemonaid'),
    )

    // this._kayttajaService.nykyinenAsiakasObservable.pipe(
    //   map(asiakas => {
    //     if (asiakas) {
    //       const today = this._dateService.currentLocalDate()
    //       const sopimuskausi = this._asiakasJaettuService.annaKausiPaivamaaralle(asiakas.sopimuskaudet, today)
    //       return sopimuskausi ? this._asiakasJaettuService.onkoSopimuskausiTauolla(sopimuskausi, today, false) : false
    //     }
    //     return false
    //   })
    // ).subscribe(tauolla => {
    //   this.sopimuskausiTauolla = tauolla
    // })

    this.selvitettaviaTositteitaObservable = this._kayttajaService.kayttajaObservable.pipe(
      switchMap(kayttaja => {
        if (!kayttaja || !kayttaja.asiakasAvain || !kayttaja.roolit[Roolit.TOSITTEET_OSTO]) {
          return observableOf<number>(0)
        }
        const polku = this._tositeUriService.annaSelvityspyynnotCollectionUri(kayttaja.asiakasAvain)
        return this._firebaseLemonaid
          .firestoreCollection<Selvityspyynto>(polku)
          .listen()
          .pipe(map(pyynnot => (pyynnot ?? []).filter(p => p.tila === 'avoin' && p.poistettu !== true && onSelvityspyyntoNakyvissaKayttajalle(kayttaja, p)).length))
      }),
    )

    // this.showNewFeatureSignAtLaskujenVastaanottoObservable = this._userSettingsService.userSettingsObservable.pipe(
    //   map(newFeatures => {
    //     if (!newFeatures?.seen[NewFeature.LASKUJEN_VASTAANOTTO]) {
    //       return true
    //     }
    //     return false
    //   })
    // )

    // this.showNewFeatureSignAtMaksutLaskujenVastaanottoObservable = this._userSettingsService.userSettingsObservable.pipe(
    //   map(newFeatures => {
    //     if (!newFeatures?.seen[NewFeature.MAKSUT_LASKUJEN_VASTAANOTTO]) {
    //       return true
    //     }
    //     return false
    //   })
    // )

    // this.showNewFeatureSignAtMaksutMaksaminenObservable = this._userSettingsService.userSettingsObservable.pipe(
    //   map(newFeatures => {
    //     if (!newFeatures?.seen[NewFeature.MAKSUT_MAKSAMINEN]) {
    //       return true
    //     }
    //     return false
    //   })
    // )

    // this.showNewFeatureSignAtLaskutusAsetuksetObservable = this._userSettingsService.userSettingsObservable.pipe(
    //   map(newFeatures => {
    //     if (!newFeatures?.seen[NewFeature.AUTOMAATTINEN_RESKONTRA]) {
    //       return true
    //     }
    //     return false
    //   })
    // )

    this._apixConfigObservable = this._kayttajaService.kayttajaObservable.pipe(
      switchMap(kayttaja => {
        if (!kayttaja || !kayttaja.roolit[Roolit.SAHKOISTEN_LASKUJEN_VASTAANOTTO]) {
          return observableOf(null)
        }
        return this._firebaseLemonaid.firestoreDoc<ApixReceivedInvoiceConfig>('customers/' + kayttaja.asiakasAvain + '/apix-received-invoice-config/' + kayttaja.asiakasAvain).listen()
      }),
    )

    const einvoiceApprovingActivatedObservable = this._apixConfigObservable.pipe(map(config => !!config && config.forwardActive && config.forwardRequiresApproving))

    this.numberOfUnapprovedEinvoicesObservable = combineLatest([this._kayttajaService.kayttajaObservable, einvoiceApprovingActivatedObservable]).pipe(
      switchMap(([kayttaja, approvingIsActive]) => {
        if (!kayttaja || !kayttaja.roolit[Roolit.SAHKOISTEN_LASKUJEN_VASTAANOTTO]) {
          return observableOf<number>(0)
        }
        if (!approvingIsActive) {
          return observableOf<number>(0)
        }
        return this._einvoicesAngularService.unapprovedEinvoicesTotalSumAndNumberObservable().pipe(map(values => values?.maara))
      }),
    )

    this.tositteetRequiringAttentionObservable = combineLatest([this.selvitettaviaTositteitaObservable, this.numberOfUnapprovedEinvoicesObservable]).pipe(
      map(([selvitettavia, unapprovedEinvoices]) => {
        return (selvitettavia || 0) + (unapprovedEinvoices || 0)
      }),
    )

    einvoiceApprovingActivatedObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(approvingIsActive => {
      this.naytaLaskujenHyvaksyminen = !!approvingIsActive
    })

    this.naytaTilinpaatoksetObservable = combineLatest([this._tilinpaatosLemonaidUsersService.tilinpaatosUsersObservable, this._kayttajaService.kayttajanTiedotObservable]).pipe(
      map(([tilinpaatosUsers, kayttaja]) => {
        if (!kayttaja) {
          return false
        }
        return this._isTilinpaatosUser(kayttaja.uid, tilinpaatosUsers)
      }),
    )

    this.naytaKerroMielipiteesiNappiObservable = this._userSettingsService.userSettingsObservable.pipe(
      map(userSettings => {
        if (!userSettings) {
          return false
        }
        const shownUntil = userSettings.shownUntil?.[NewFeature.ASIAKASKYSELY]
        if (!shownUntil) {
          return false
        }
        return this._dateService.compareLocalDates(shownUntil, '>', this._dateService.currentLocalDate())
      }),
    )

    this.onToistuviaLaskujaObservable = this._kayttajaService.nykyinenAsiakasObservable.pipe(
      switchMap(asiakas => {
        if (!asiakas) {
          return observableOf(false)
        }
        const toistuvaLaskuUri = this._laskuUriService.getToistuvatLaskutCollectionUri(asiakas.asiakasAvain)
        return this._firebaseLemonaid
          .firestoreCollection<ToistuvaLasku>(toistuvaLaskuUri)
          .where('poistettu', '==', false)
          .limit(1)
          .listenSnapshots()
          .pipe(map(snapshots => snapshots.length > 0))
      }),
    )

    // Lemonator merkitsee moniyritystuen käyttäjät lipulla, joten muille ei tehdä turhaa palvelinkutsua jokaisella latauksella.
    this.vaihdettavatYrityksetObservable = this._kayttajaService.kayttajaObservable.pipe(
      map(kayttaja => (kayttaja?.moniyritys ? kayttaja.avain : null)),
      distinctUntilChanged(),
      switchMap(kayttajaAvain => {
        if (!kayttajaAvain) {
          return observableOf(null)
        }
        return from(this._firebaseLemonaid.functionsCall<void, VaihdettavatYrityksetVastaus>('loginVaihdettavatYritykset', null)).pipe(
          map(vastaus => (vastaus?.nykyinenYritys && vastaus.vaihdettavatYritykset?.length ? vastaus : null)),
          catchError(err => {
            this._errorHandler.handleError(err)
            return observableOf(null)
          }),
        )
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    )
  }

  async vaihdaYritysta() {
    const vaihdettavat = await firstValueFrom(this.vaihdettavatYrityksetObservable)
    if (!vaihdettavat) {
      return
    }
    this.suljeVasenValikko()
    const data: VaihdaYritysDialogData = {
      nykyinenYritys: vaihdettavat.nykyinenYritys,
      vaihdettavatYritykset: vaihdettavat.vaihdettavatYritykset,
    }
    this._dialog.open(VaihdaYritysDialog, { data: data, autoFocus: false, maxWidth: '450px', width: '92vw' })
  }

  suljeVasenValikko() {
    this.vasemmanValikonSulkupyynto.emit()
  }

  ngOnInit() {}

  etusivulle() {
    this._router.navigate(['/'])
  }

  logout() {
    this._ladataanService.aloitaLataaminen()
    this.suljeVasenValikko()
    this._kayttajaService
      .logout()
      .then(res => {
        this._router
          .navigate(['/kirjaudu'])
          .then(() => {
            this._ladataanService.lopetaLataaminen()
          })
          .catch(err => {
            this._ladataanService.lopetaLataaminen()
            this._errorHandler.handleError(err)
          })
      })
      .catch(err => {
        this._ladataanService.lopetaLataaminen()
        this._errorHandler.handleError(err)
      })
  }

  vaihdaKieli() {
    this.suljeVasenValikko()
    const data: VaihdaKieliDialogData = {
      tallenna: true,
    }
    this._dialog.open(VaihdaKieliDialog, { data: data, autoFocus: false })
  }

  vaihdaSalasana() {
    this.suljeVasenValikko()
    const data: VaihdaSalasanaDialogData = {}
    this._dialog.open(VaihdaSalasanaDialog, { data: data, autoFocus: false, maxWidth: '350px' })
  }

  async avaaAsiakasKyselyDialog() {
    const kayttajanTiedot = await firstValueFrom(this._kayttajaService.kayttajanTiedotObservable)
    if (!kayttajanTiedot?.uid || !kayttajanTiedot?.asiakasAvain) {
      return
    }
    const userSettings = await firstValueFrom(this._userSettingsService.userSettingsObservable)
    const shownUntilLocal = userSettings?.shownUntil?.[NewFeature.ASIAKASKYSELY]
    const voimassaAstiLocal = shownUntilLocal || this._dateService.lisaaPaiviaPaikallinen(this._dateService.currentLocalDate(), 14)
    const voimassaAsti = this._dateService.localDateToDate(voimassaAstiLocal)

    const data: AsiakasKyselyDialogData = {
      kayttajaUid: kayttajanTiedot.uid,
      kayttajaAsiakasAvain: kayttajanTiedot.asiakasAvain,
      skipShownUntilUpdate: true,
      voimassaAsti: voimassaAsti,
    }
    this._dialog.open(AsiakasKyselyDialog, { data: data, autoFocus: false })
  }

  vaihdaAlavalikko(valittu: Alavalikko) {
    this.valittuAlavalikko = valittu

    if (this.valittuAlavalikko === 'laskutus') {
      this._router.navigate(['laskutus', 'laskut'])
    } else if (this.valittuAlavalikko === 'tositteet') {
      this._router.navigate(['tositteet', 'osto'])
    } else if (this.valittuAlavalikko === 'palkat') {
      this._router.navigate(['palkat'])
    } else if (this.valittuAlavalikko === 'raportit') {
      this._router.navigate(this.naytaKirjanpito ? ['kirjanpidon-raportit'] : ['tilinpaatokset'])
    } else if (this.valittuAlavalikko === 'maksut') {
      // // The doc is already here, so no additional read is required
      // firstValueFrom(this.showNewFeatureSignAtMaksutLaskujenVastaanottoObservable).then(isVisible => {
      //   if (isVisible) {
      //     this.updateSeen(NewFeature.MAKSUT_LASKUJEN_VASTAANOTTO)
      //   }
      // })

      // firstValueFrom(
      //   combineLatest([this.showNewFeatureSignAtMaksutMaksaminenObservable, this._apixConfigObservable])
      // ).then(([isVisible, config]) => {
      //   if (isVisible && (!config || !config?.paymentReceiveIsActive || config?.paymentIsActive || config?.paymentServiceOrdered)) {
      //     this.updateSeen(NewFeature.MAKSUT_MAKSAMINEN)
      //   }
      // })

      this._router.navigate(['maksut'])
    }
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  async updateSeen(seenFeature: NewFeature) {
    const kayttajanTiedot = await firstValueFrom(this._kayttajaService.kayttajanTiedotObservable)
    if (!kayttajanTiedot?.asiakasAvain) {
      return
    }

    return this._userSettingsService.setFeatureSeen(seenFeature, kayttajanTiedot.asiakasAvain, kayttajanTiedot.uid, this._firebaseLemonaid)
  }

  private _isTilinpaatosUser(kayttajaAvain: string, tilinpaatosUsers: TilinpaatosUserData) {
    const userAsShareholder = tilinpaatosUsers?.osakkaatUsers?.find(shareholder => shareholder.lemonaidUid === kayttajaAvain)
    if (userAsShareholder) {
      return true
    }
    const userAsBoardMember = tilinpaatosUsers?.hallitusUsers?.find(boardMember => boardMember.lemonaidUid === kayttajaAvain)
    if (userAsBoardMember) {
      return true
    }
    return false
  }
}
